"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { SlideUpPage } from "@/components/layout/SlideUpPage";

interface CreateCompanyFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const INDUSTRIES = [
  "IT og Teknologi",
  "Bygg og Anlegg",
  "Helse og Omsorg",
  "Service og Handel",
  "Industri og Produksjon",
  "Transport og Logistikk",
  "Utdanning",
  "Finans",
  "Media og Kommunikasjon",
  "Annet",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];

export function CreateCompanyForm({ onClose, onSuccess }: CreateCompanyFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    orgNumber: "",
    description: "",
    industry: INDUSTRIES[0],
    size: COMPANY_SIZES[0],
    location: "",
    website: "",
    logo: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // TODO: Replace with actual Convex mutation when ready
      // const result = await createCompany(formData);
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      console.log("Company created:", formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Kunne ikke opprette firma");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideUpPage onClose={onClose} showCloseButton={true}>
      <div className="min-h-full p-6" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        {/* Header */}
        <div className="mb-6 pt-10">
          <h2
            style={{
              fontSize: "var(--font-size-title-md)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-text-primary)",
            }}
          >
            Opprett firmaprofil
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Firmanavn */}
          <div>
            <label
              htmlFor="name"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Firmanavn *
            </label>
            <input
              type="text"
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="Skriv firmaets navn"
            />
          </div>

          {/* Organisasjonsnummer */}
          <div>
            <label
              htmlFor="orgNumber"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Organisasjonsnummer *
            </label>
            <input
              type="text"
              id="orgNumber"
              required
              value={formData.orgNumber}
              onChange={(e) => setFormData({ ...formData, orgNumber: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="9 siffer"
              maxLength={9}
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label
              htmlFor="description"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Beskrivelse *
            </label>
            <textarea
              id="description"
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="Fortell om firmaet..."
            />
          </div>

          {/* Bransje */}
          <div>
            <label
              htmlFor="industry"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Bransje *
            </label>
            <select
              id="industry"
              required
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
            >
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          {/* Lokasjon */}
          <div>
            <label
              htmlFor="location"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Lokasjon *
            </label>
            <input
              type="text"
              id="location"
              required
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="Oslo, Bergen, etc."
            />
          </div>

          {/* Størrelse */}
          <div>
            <label
              htmlFor="size"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Antall ansatte *
            </label>
            <select
              id="size"
              required
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
            >
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} ansatte
                </option>
              ))}
            </select>
          </div>

          {/* Nettside (valgfritt) */}
          <div>
            <label
              htmlFor="website"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Nettside (valgfritt)
            </label>
            <input
              type="url"
              id="website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="https://example.com"
            />
          </div>

          {/* Logo URL (valgfritt) */}
          <div>
            <label
              htmlFor="logo"
              className="mb-1.5 block"
              style={{
                fontSize: "var(--font-size-body-md)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-primary)",
              }}
            >
              Logo URL (valgfritt)
            </label>
            <input
              type="url"
              id="logo"
              value={formData.logo}
              onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              className="w-full rounded-lg border px-4 py-2.5 transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="https://example.com/logo.png"
            />
          </div>

          {/* Error message */}
          {error && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold transition-colors"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                color: "var(--color-text-primary)",
              }}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold transition-colors"
              style={{
                backgroundColor: "var(--color-bg-accent)",
                color: "var(--color-text-primary)",
              }}
            >
              {isSubmitting ? "Oppretter..." : "Opprett firma"}
            </Button>
          </div>
        </form>
      </div>
    </SlideUpPage>
  );
}

